import React from 'react';
import { Star, AlertCircle, CreditCard } from 'lucide-react';

interface SubscriptionPlan {
  name: string;
}

interface SubscriptionBannerProps {
  hasValidSubscription: boolean;
  isLifetime: boolean;
  currentPlan: SubscriptionPlan | null;
  childrenCount: number;
  maxChildren: number;
  onSubscribeClick: () => void;
  onUpgradeClick: () => void;
}

export function SubscriptionBanner({
  hasValidSubscription,
  isLifetime,
  currentPlan,
  childrenCount,
  maxChildren,
  onSubscribeClick,
  onUpgradeClick
}: SubscriptionBannerProps) {
  // No subscription
  if (!hasValidSubscription) {
    return (
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
        <div className="flex items-center space-x-3">
          <AlertCircle className="w-8 h-8 text-orange-600" />
          <div>
            <h3 className="font-semibold text-orange-800">No Active Subscription</h3>
            <p className="text-orange-600 text-sm">
              Subscribe to start adding children and accessing all features
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Has subscription
  if (!currentPlan) return null;

  return (
    <div className={`bg-gradient-to-r border rounded-xl p-4 ${
      isLifetime
        ? 'from-purple-50 to-gold-50 border-purple-200'
        : 'from-green-50 to-blue-50 border-green-200'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isLifetime
              ? 'bg-purple-100'
              : 'bg-green-100'
          }`}>
            <Star className={`w-5 h-5 ${
              isLifetime
                ? 'text-purple-600'
                : 'text-green-600'
            }`} />
          </div>
          <div>
            <h3 className={`font-semibold ${
              isLifetime
                ? 'text-purple-800'
                : 'text-green-800'
            }`}>
              {currentPlan.name}
              {isLifetime && <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-1 rounded-full">LIFETIME</span>}
            </h3>
            <p className={`text-sm ${
              isLifetime
                ? 'text-purple-600'
                : 'text-green-600'
            }`}>
              {isLifetime ? (
                `${childrenCount} children added (unlimited)`
              ) : (
                `${childrenCount} of ${maxChildren} ${maxChildren === 1 ? 'child' : 'children'} added`
              )}
            </p>
          </div>
        </div>
        {!isLifetime && childrenCount >= maxChildren && (
          <button
            onClick={onUpgradeClick}
            className="text-blue-600 hover:text-blue-500 text-sm font-medium"
          >
            Upgrade Plan →
          </button>
        )}
      </div>
    </div>
  );
}

interface HeaderActionsProps {
  hasValidSubscription: boolean;
  canAddChild: boolean;
  onSubscribeClick: () => void;
  onWizardClick: () => void;
  onAddChildClick: () => void;
}

export function HeaderActions({
  hasValidSubscription,
  canAddChild,
  onSubscribeClick,
  onWizardClick,
  onAddChildClick
}: HeaderActionsProps) {
  return (
    <div className="flex items-center space-x-4">
      {!hasValidSubscription && (
        <button
          onClick={onSubscribeClick}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 hover:to-red-700 transition-all shadow-lg font-baloo"
        >
          <CreditCard className="w-5 h-5" />
          <span>Subscribe Now</span>
        </button>
      )}
      <button
        onClick={onWizardClick}
        className="flex items-center space-x-2 px-6 py-3 rounded-xl transition-colors shadow-lg font-baloo bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
      >
        <Star className="w-5 h-5" />
        <span>Quick Start Wizard</span>
      </button>
      <button
        onClick={canAddChild ? onAddChildClick : undefined}
        disabled={!canAddChild}
        className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-colors shadow-lg font-baloo ${
          canAddChild
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        <span>Add Child</span>
      </button>
    </div>
  );
}
